<!-- eslint-disable max-len -->
<template>
  <div>
    <!-- Latest live -->
    <p class="text-[11px] font-bold uppercase tracking-wider text-gray-400">
      {{ t('livvia.postLive.sectionLatest') }}
    </p>
    <button
      type="button"
      class="mt-2 flex w-full cursor-pointer items-center gap-3 rounded-2xl border-2 border-teal-500 bg-white p-3 text-left shadow-sm ring-2 ring-teal-100 transition hover:brightness-[1.02] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600"
      @click="onLatestClick"
    >
      <div class="relative flex h-16 w-24 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-gradient-to-br from-orange-300 via-pink-500 to-purple-600">
        <span class="flex size-7 items-center justify-center rounded-full bg-white/90">
          <PlayIcon class="ml-0.5 size-3.5 text-gray-900" />
        </span>
        <span class="absolute bottom-1 right-1 rounded-md bg-black/70 px-1.5 py-0.5 text-[10px] font-bold text-white">
          {{ latest.duration }}
        </span>
      </div>
      <div class="min-w-0 flex-1">
        <p class="truncate font-semibold text-gray-900">
          {{ latest.title }}
        </p>
        <p class="mt-0.5 truncate text-xs text-gray-500">
          {{ latest.subtitle }}
        </p>
      </div>
      <span class="ml-auto shrink-0 rounded-full bg-teal-50 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-teal-700">
        {{ t('livvia.postLive.tagNew') }}
      </span>
    </button>

    <!-- Previous lives -->
    <p class="mt-6 text-[11px] font-bold uppercase tracking-wider text-gray-400">
      {{ t('livvia.postLive.sectionPrevious') }}
    </p>
    <div class="mt-2 space-y-2">
      <div
        v-for="live in previous"
        :key="live.id"
        class="flex items-center gap-3 rounded-2xl bg-white p-3 shadow-sm ring-1 ring-gray-200"
      >
        <div
          class="relative flex h-16 w-24 shrink-0 items-center justify-center overflow-hidden rounded-xl"
          :class="live.thumbClass"
        >
          <span class="flex size-7 items-center justify-center rounded-full bg-white/90">
            <PlayIcon class="ml-0.5 size-3.5 text-gray-900" />
          </span>
          <span class="absolute bottom-1 right-1 rounded-md bg-black/70 px-1.5 py-0.5 text-[10px] font-bold text-white">
            {{ live.duration }}
          </span>
        </div>
        <div class="min-w-0 flex-1">
          <p class="truncate font-semibold text-gray-900">
            {{ live.title }}
          </p>
          <p class="mt-0.5 truncate text-xs text-gray-500">
            {{ live.subtitle }}
          </p>
        </div>
        <span class="ml-auto inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-50 px-3 py-1 text-[11px] font-semibold text-emerald-700">
          <CheckIcon class="size-3" />
          {{ t('livvia.postLive.tagCarts', { count: live.cartsCount }) }}
        </span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { PlayIcon } from '@heroicons/vue/24/solid';
import { CheckIcon } from '@heroicons/vue/24/outline';
import useLivviaStore from '@/store/livvia';

const { t } = useI18n();
const livvia = useLivviaStore();

// TODO: replace mock data with real InstagramLiveMetadata / FacebookLiveMetadata
// fetched from the plugin store when we finalise post-live detection (Q1).
const latest = computed(() => ({
  title: livvia.lastLive?.timestampLabel || t('livvia.postLive.mock.latestTitle'),
  subtitle: t('livvia.postLive.mock.latestSubtitle', {
    comments: livvia.lastLive?.commentsCount ?? 340,
    platform: livvia.lastLive?.platform === 'facebook' ? 'Facebook' : 'Instagram',
  }),
  duration: livvia.lastLive?.durationLabel || '1h 42',
}));

// Previous-lives strip is placeholder-only until the plugin store's
// live-metadata endpoint is wired here. Kept behind a DEV gate so a
// Natura/Boticário demo never sees hardcoded fake counts like "31 carts".
// TODO: replace with real InstagramLiveMetadata / FacebookLiveMetadata
// once post-live detection (Q1) lands; then drop the DEV gate.
const IS_DEV_PREVIEW = process.env.NODE_ENV !== 'production';

const previous = IS_DEV_PREVIEW ? [
  {
    id: 'p1',
    title: t('livvia.postLive.mock.previous1Title'),
    subtitle: t('livvia.postLive.mock.previous1Subtitle'),
    duration: '2h 05',
    thumbClass: 'bg-gradient-to-br from-sky-400 via-indigo-500 to-purple-600',
    cartsCount: 31,
  },
  {
    id: 'p2',
    title: t('livvia.postLive.mock.previous2Title'),
    subtitle: t('livvia.postLive.mock.previous2Subtitle'),
    duration: '58 min',
    thumbClass: 'bg-gradient-to-br from-teal-400 via-sky-500 to-indigo-500',
    cartsCount: 24,
  },
] : [];

const onLatestClick = () => {
  livvia.startBuildingCarts();
};
</script>
